import { Button, ErrorBoundary, Illustration, Text, useLoading, VStack } from '@genial/components';
import { Config } from '@genial/config';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { useNavigation, useIsFocused } from '@react-navigation/native';
import { Box, View } from 'native-base';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useToastCreator } from '../../hooks/useToastCreator';
import { sessionState } from '../../store/session/atom';

interface AdemiconConsortiumProps {}

export const AdemiconConsortium: React.FC<AdemiconConsortiumProps> = () => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isFocused = useIsFocused();

  const { refreshToken } = useRecoilValue(sessionState);
  const { showToast } = useToastCreator('ademicon-consortium-toast');
  const { goBack, canGoBack } = useNavigation();
  const { hide, show } = useLoading();

  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleGoBack = () => {
    if (canGoBack()) {
      goBack();
    }
  };

  const handleRequestConsortium = async () => {
    try {
      setIsLoading(true);
      setIsError(false);
      const breakLine = isMobile ? '\n' : '';

      show({
        title: `Estamos carregando sua solicitação.${breakLine} Por favor, aguarde um momento.`,
        description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
      });

      const response = await fetch(`${Config.OriginUrlApi}/User/code`, {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
        headers: { 'content-Type': 'application/json' },
        mode: 'cors',
        credentials: 'same-origin',
      });

      if (response.status === 201) {
        const body = await response.text();
        const apiUrl = Utils.Normalize.ApiUrl(Config.GenialAdemiconPortalUrl);

        const url = `${apiUrl}/sso?code=${body}`;

        if (isMobile) {
          window.location.href = url;
        } else {
          window.open(url, '_blank');
        }

        setTimeout(() => {
          handleGoBack();
        }, 5000);
      } else {
        throw new Error('Houve um erro ao processar sua solicitação.');
      }
    } catch (error) {
      console.log('error', error);
      setIsError(true);
      showToast(
        'error',
        'Erro ao processar sua solicitação.',
        'Ocorreu um erro ao carregar informações,\nverifique sua conexão com a internet e\ntente novamente em instantes.'
      );
    } finally {
      setIsLoading(false);
      hide();
    }
  };

  useEffect(() => {
    handleRequestConsortium();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  const ErroComponent = (
    <ErrorBoundary
      onRefresh={handleRequestConsortium}
      errorIconOrIllustration="WarningIllustration"
      errorTitle="Houve um erro ao processar sua solicitação."
      errorMessage="Não foi possível processar sua solicitação. Por favor, tente novamente mais tarde."
    />
  );

  const SuccessComponent = (
    <VStack space={Spacing['inline.xxxs']} alignItems="center" justifyContent="center">
      <Illustration name="Redirect" />
      <Text
        color="neutral.black"
        fontSize={Typography['fontsize.lg']}
        variant="semibold"
        textAlign="center">
        Sua solicitação foi aberta em uma nova aba.
      </Text>
      <Text textAlign="center" color="neutral.black">
        Você será redirecionado para a página inicial em instantes, se isso não acontecer,
        <Button
          onPress={handleGoBack}
          variant="textLink"
          p={0}
          _text={{ textTransform: 'lowercase' }}>
          {' clique aqui '}
        </Button>
        para voltar.
      </Text>
    </VStack>
  );

  return (
    <Box flex={1} bg="background">
      <View minW="container">
        <VStack flex={1} px={Spacing['inline.xxxs']} justifyContent="center">
          {!isLoading && isError && ErroComponent}
          {!isLoading && !isError && SuccessComponent}
        </VStack>
      </View>
    </Box>
  );
};
